import { useEffect, useRef, useState } from 'react';
import { ModalCloseButton, useToast } from '@busy/ui';
import { useCutTimer } from '../../store/cutTimer';
import { useT } from '../../lib/i18n';
import { stitchTransformation, type StitchSource } from '../../lib/videoStitch';
import { logPhotoShareConsent } from '@busy/api';

// Post-after reveal screen. Sits between AfterPhotoStep and PostCutSummary
// (mode='reveal' in the cut-timer state machine). Generates the
// transformation video on entry, plays it inline, and offers:
//
//   - Share — opens the OS share sheet with the video file. Requires an
//     explicit consent tap first ("I have permission") to comply with the
//     same client-photo consent rules the gallery share enforces.
//   - Save to phone — Web Share with "save to files" / direct download.
//     iOS users can also long-press the inline video to save to camera
//     roll; we surface that as a hint.
//   - Continue to wrap-up — leaves without sharing. The achievement still
//     counts because the video was generated.
//
// The transformation video is also marked as "generated" on the store so
// PostCutSummary can credit it to the Transformations achievement at
// visit-commit time. Skipping this step (Skip & wrap-up on AfterPhotoStep)
// bypasses generation entirely, so no credit is awarded.

export function TransformationRevealStep() {
  const t = useT();
  const toast = useToast();
  const beforePhotoBlob = useCutTimer((s) => s.beforePhotoBlob);
  const afterPhotoBlob = useCutTimer((s) => s.afterPhotoBlob);
  const beforeVideoBlob = useCutTimer((s) => s.beforeVideoBlob);
  const afterVideoBlob = useCutTimer((s) => s.afterVideoBlob);
  // Pan/zoom crop rects set during capture — passed straight through
  // to the stitch so what the barber composed in the crop UI is what
  // ends up in the transformation video.
  const beforeVideoCrop = useCutTimer((s) => s.beforeVideoCrop);
  const afterVideoCrop = useCutTimer((s) => s.afterVideoCrop);
  const beforePhotoCrop = useCutTimer((s) => s.beforePhotoCrop);
  const afterPhotoCrop = useCutTimer((s) => s.afterPhotoCrop);
  const beforePhotoSource = useCutTimer((s) => s.beforePhotoSource);
  const afterPhotoSource = useCutTimer((s) => s.afterPhotoSource);
  const goToSummary = useCutTimer((s) => s.goToSummaryFromReveal);
  const closeModal = useCutTimer((s) => s.closeModal);
  const markTransformationGenerated = useCutTimer((s) => s.markTransformationGenerated);
  const client = useCutTimer((s) => s.client);

  const [progress, setProgress] = useState(0);
  const [generating, setGenerating] = useState(true);
  const [output, setOutput] = useState<{ blob: Blob; url: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [consentChecked, setConsentChecked] = useState(false);
  const [busy, setBusy] = useState(false);
  const startedRef = useRef(false);

  const clientName =
    client?.kind === 'existing'
      ? `${client.firstName ?? ''} ${client.lastName ?? ''}`.trim() || t('today.unnamed')
      : t('today.unnamed');
  const clientId = client?.kind === 'existing' ? client.id : null;

  // Generate exactly once on mount. The cut-timer state machine routes us
  // here only after at least an after photo/video was captured.
  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    // Video path: prefer the video blob + its capture-time crop.
    // Photo fallback: pass the cropped photo blob + the photo's crop
    // rect translated against its native source. For the photo path
    // we use the cropped blob (no native source needed for stitch —
    // its natural dimensions ARE the crop output), so no crop coords
    // travel through the stitch for photos. Video carries crop coords
    // because the source blob is the FULL recording.
    const before = pickSource(
      beforeVideoBlob,
      beforeVideoCrop,
      beforePhotoBlob,
      beforePhotoSource,
      beforePhotoCrop,
    );
    const after = pickSource(
      afterVideoBlob,
      afterVideoCrop,
      afterPhotoBlob,
      afterPhotoSource,
      afterPhotoCrop,
    );

    // The reveal screen requires AT LEAST an after. If neither side has
    // anything we shouldn't even be here — bail out gracefully.
    if (!after) {
      setError(t('reveal.errorNoAfter'));
      setGenerating(false);
      return;
    }
    // If only after exists, skip stitch and just play the after directly.
    if (!before) {
      const url = URL.createObjectURL(after.blob);
      const blob = after.blob;
      setOutput({ blob, url });
      setGenerating(false);
      markTransformationGenerated();
      return;
    }

    let cancelled = false;
    void (async () => {
      try {
        const blob = await stitchTransformation({
          before,
          after,
          onProgress: (p) => {
            if (!cancelled) setProgress(p);
          },
        });
        if (cancelled) return;
        const url = URL.createObjectURL(blob);
        setOutput({ blob, url });
        markTransformationGenerated();
      } catch (err) {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : String(err);
        setError(msg);
      } finally {
        if (!cancelled) setGenerating(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Revoke the output URL when the modal closes.
  useEffect(() => {
    return () => {
      if (output) URL.revokeObjectURL(output.url);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleShare() {
    if (!output || !consentChecked) return;
    const ext = output.blob.type.includes('mp4') ? 'mp4' : 'webm';
    // Filename, title, and caption are ALL name-free. Filenames travel
    // with the file across email / MMS / IG / Files — anywhere the
    // client's name could end up in front of strangers. Privacy
    // commitment per feedback_no_client_names_in_captions.md.
    const fileName = `transformation-${Date.now()}.${ext}`;
    const file = new File([output.blob], fileName, { type: output.blob.type || 'video/mp4' });
    const nav = typeof navigator !== 'undefined' ? (navigator as Navigator) : null;
    if (!nav?.share) {
      toast(t('photoShare.unsupported'), 'error');
      return;
    }
    if (nav.canShare && !nav.canShare({ files: [file] })) {
      toast(t('reveal.shareRejectedHint'), 'error');
      return;
    }
    setBusy(true);
    // Audit log keeps clientName because it's local data — only the
    // outgoing share payload above is name-stripped.
    void logPhotoShareConsent({
      clientId,
      clientName,
      photoUrl: null,
      shareTarget: 'transformation_reveal',
    });
    try {
      await nav.share({
        title: t('reveal.shareTitle'),
        text: t('reveal.shareCaption'),
        files: [file],
      });
      toast(t('reveal.sharedToast'), 'success');
    } catch {
      // User cancelled — keep modal open.
    } finally {
      setBusy(false);
    }
  }

  // "Save to phone" — anchor download. On iOS this drops the file in
  // Files; from there the user can save to camera roll. On Android Chrome
  // it triggers the standard download flow.
  function handleSave() {
    if (!output) return;
    const ext = output.blob.type.includes('mp4') ? 'mp4' : 'webm';
    const a = document.createElement('a');
    a.href = output.url;
    // Save-to-phone filename is also name-free — it lands in the user's
    // Files / Downloads where it may get re-shared later. Keep PII out.
    a.download = `transformation-${Date.now()}.${ext}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    toast(t('reveal.savedToast'), 'success');
  }

  return (
    <>
      <div className="bg-navy-900 text-white px-6 pt-7 pb-6 rounded-t-3xl relative">
        <ModalCloseButton onClose={closeModal} variant="dark" />
        <div className="text-[11px] tracking-[0.18em] uppercase font-bold text-gold-400 inline-flex items-center gap-1.5 mt-2 mb-2">
          <SparkleIcon className="w-3 h-3" />
          {t('reveal.eyebrow')}
        </div>
        <h2 className="font-display text-3xl leading-tight">{t('reveal.title')}</h2>
        <p className="text-white/72 text-sm leading-relaxed mt-2 max-w-sm">
          {generating ? t('reveal.generatingBody') : t('reveal.body')}
        </p>
      </div>

      <div className="p-5 space-y-3">
        {error ? (
          <div className="rounded-xl border border-red-500/40 bg-red-50 dark:bg-red-600/10 p-3 text-sm text-red-900 dark:text-red-200">
            {t('reveal.error', { message: error })}
          </div>
        ) : generating ? (
          // Generation loading state — full-bleed 9:16 placeholder with a
          // progress bar so the barber knows something is happening.
          <div className="rounded-2xl overflow-hidden bg-navy-900 aspect-[9/16] max-h-[60dvh] mx-auto relative grid place-items-center">
            <div className="text-center px-6 space-y-4">
              <SparkleIcon className="w-10 h-10 text-gold-500 mx-auto animate-pulse" />
              <div className="text-white text-base font-display">
                {t('reveal.generating')}
              </div>
              <div className="h-1.5 bg-white/10 rounded overflow-hidden w-56 mx-auto">
                <div
                  className="h-full bg-gold-500 transition-[width] duration-200"
                  style={{ width: `${Math.round(progress * 100)}%` }}
                />
              </div>
              <p className="text-[11px] text-white/50 leading-snug max-w-xs mx-auto">
                {t('reveal.generatingHint')}
              </p>
            </div>
          </div>
        ) : output ? (
          <div className="rounded-2xl overflow-hidden border border-line bg-black aspect-[9/16] max-h-[60dvh] mx-auto">
            <video
              src={output.url}
              controls
              autoPlay
              loop
              playsInline
              className="w-full h-full object-cover"
            />
          </div>
        ) : null}

        {/* Save-to-phone hint for iOS — long-press the inline player
            to save to camera roll. */}
        {!generating && output && (
          <p className="text-[11px] text-ink-500 leading-snug text-center">
            {t('reveal.iosSaveHint')}
          </p>
        )}

        {/* Consent-gated share — same UX as SharePhotoModal so the
            consent rule is consistent everywhere. We hide the share row
            entirely while generating so it doesn't feel premature. */}
        {!generating && output && (
          <div className="rounded-xl border border-line bg-bg-alt p-3 space-y-3">
            <p className="text-[12px] font-bold text-ink-700 leading-snug">
              {t('photoShare.warnTitle')}
            </p>
            <p className="text-[11px] text-ink-500 leading-snug">
              {t('photoShare.warnBody')}
            </p>
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={consentChecked}
                onChange={(e) => setConsentChecked(e.target.checked)}
                className="mt-0.5 accent-gold-500"
              />
              <span className="text-[12px] font-semibold text-ink-700 leading-snug">
                {t('photoShare.consentCheckbox')}
              </span>
            </label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={handleShare}
                disabled={!consentChecked || busy}
                className="h-11 rounded-lg bg-navy-900 text-white dark:bg-gold-500 dark:text-navy-900 font-bold text-sm inline-flex items-center justify-center gap-1.5 disabled:opacity-50"
              >
                <ShareIcon className="w-4 h-4" />
                {t('reveal.share')}
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={busy}
                className="h-11 rounded-lg border border-line bg-surface text-ink-700 font-bold text-sm inline-flex items-center justify-center gap-1.5 disabled:opacity-50"
              >
                <DownloadIcon className="w-4 h-4" />
                {t('reveal.save')}
              </button>
            </div>
          </div>
        )}

        <button
          type="button"
          onClick={goToSummary}
          disabled={generating}
          className="w-full h-12 rounded-xl bg-navy-900 text-white dark:bg-gold-500 dark:text-navy-900 font-bold text-[15px] inline-flex items-center justify-center gap-2 disabled:opacity-50"
        >
          {t('reveal.continue')}
          <ChevronRight />
        </button>
      </div>
    </>
  );
}

/** Resolve which captured source to hand to the stitch for one half.
 *  Video wins over photo (the dynamic asset is the better share moment).
 *  Photo path: prefer the native source + crop rect (single-pass JPEG
 *  through the stitch instead of two), fall back to the cropped blob
 *  with no crop rect when the native source wasn't captured. */
function pickSource(
  videoBlob: Blob | null,
  videoCrop: { x: number; y: number; width: number; height: number } | null,
  photoBlob: Blob | null,
  photoSource: Blob | null,
  photoCrop: { x: number; y: number; width: number; height: number } | null,
): StitchSource | null {
  if (videoBlob) return { kind: 'video', blob: videoBlob, crop: videoCrop ?? null };
  if (photoSource && photoCrop) {
    return { kind: 'photo', blob: photoSource, crop: photoCrop };
  }
  if (photoBlob) return { kind: 'photo', blob: photoBlob, crop: null };
  return null;
}

function ChevronRight() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

function ShareIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
      <polyline points="16 6 12 2 8 6" />
      <line x1="12" y1="2" x2="12" y2="15" />
    </svg>
  );
}

function DownloadIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}

function SparkleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M12 2v6m0 8v6m-10-10h6m8 0h6" />
      <path d="M5 5l3 3m8 8l3 3m0-14l-3 3m-8 8l-3 3" />
    </svg>
  );
}
